/**
 * Burn History Service
 *
 * Story BL-056.1: Burn History Persistence
 *
 * API client for oracle-bridge burn-history endpoints.
 * Uses memberAuthenticatedFetch for GET/POST/PATCH so every call carries the
 * identity-service Bearer JWT. Same pattern as userProfileService.ts (bl-791).
 */

import { getOracleBridgeUrl } from '@/utils/oracleBridge';
// dac-001-4a (2026-06-19): the GET read path routes through
// memberAuthenticatedFetch so it carries the identity-service Authorization:
// Bearer JWT (raw cookie-only fetch 401s once OB_AUTH_COOKIE_FALLBACK is OFF —
// the member session is JWT-native per ADR-026).
// bl-799 (2026-07-09): the POST/PATCH WRITE paths now ALSO route through
// memberAuthenticatedFetch, matching the bl-791 user-profile cutover. The old
// cookie-only `csrfFetch` 401'd Bearer-native identity-service members (no
// oracle-bridge `access_token` cookie) at requireSessionAuth's cookie fallback.
// The paired oracle-bridge change swaps the burn write routes' strict
// `csrfProtection` → `optionalCsrfProtection`, which skips CSRF in Bearer mode
// while preserving it for the legacy cookie path — so no hard cutover.
import { memberAuthenticatedFetch } from './member-authenticated-fetch';

// =============================================================================
// Types
// =============================================================================

export interface BurnHistoryRecord {
  id: string;
  user_id: string;
  amount_e8s: string;
  tx_index: string;
  status: 'pending' | 'confirmed' | 'failed';
  created_at: string;
  updated_at: string;
}

export interface BurnHistoryResponse {
  records: BurnHistoryRecord[];
  total: number;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Record a new burn with status 'pending'.
 * Calls POST /api/burn-history.
 */
export async function recordBurn(
  amountE8s: string,
  txIndex: string
): Promise<{ id: string; created_at: string }> {
  const baseUrl = getOracleBridgeUrl();
  const response = await memberAuthenticatedFetch(`${baseUrl}/api/burn-history`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      amount_e8s: amountE8s,
      tx_index: txIndex,
      status: 'pending',
    }),
    credentials: 'include',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || data.error || 'Failed to record burn');
  }

  return response.json();
}

/**
 * Confirm a burn record.
 * Calls PATCH /api/burn-history/:id with status 'confirmed'.
 */
export async function confirmBurn(id: string, txIndex: string): Promise<void> {
  const baseUrl = getOracleBridgeUrl();
  const response = await memberAuthenticatedFetch(`${baseUrl}/api/burn-history/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: 'confirmed', tx_index: txIndex }),
    credentials: 'include',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || data.error || 'Failed to confirm burn');
  }
}

/**
 * Mark a burn record as failed.
 * Calls PATCH /api/burn-history/:id with status 'failed'.
 */
export async function failBurn(id: string): Promise<void> {
  const baseUrl = getOracleBridgeUrl();
  const response = await memberAuthenticatedFetch(`${baseUrl}/api/burn-history/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: 'failed' }),
    credentials: 'include',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || data.error || 'Failed to mark burn as failed');
  }
}

/**
 * Fetch the authenticated user's burn history.
 * Calls GET /api/burn-history with query params.
 */
export async function fetchBurnHistory(
  limit?: number,
  offset?: number
): Promise<BurnHistoryResponse> {
  const baseUrl = getOracleBridgeUrl();
  const params = new URLSearchParams();
  if (limit !== undefined) params.set('limit', String(limit));
  if (offset !== undefined) params.set('offset', String(offset));

  const queryString = params.toString();
  const url = `${baseUrl}/api/burn-history${queryString ? `?${queryString}` : ''}`;

  const response = await memberAuthenticatedFetch(url);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || data.error || 'Failed to fetch burn history');
  }

  return response.json();
}
